import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../../api";
import { Button } from "../../../components/button";
import { Card, CardContent } from "../../../components/card";
import { CommandBar } from "../../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../../components/dashboard";
import { Editor, EditorDescription, EditorTitle } from "../../../components/editor";
import { TextInput } from "../../../components/form";
import { Icon } from "../../../components/icon";
import { Section } from "../../../components/layout";
import { ExternalLink, Link } from "../../../components/link";
import { BlockCodeWrapper, DashboardTextHeading, MutedText, Paragraph } from "../../../components/text";
import { TimeSpanPicker } from "../../../components/time-span-picker";
import { displayFormControl } from "../../../ui/alert";
import { formatDuration } from "../../../ui/format";
import { runOperation } from "../../../ui/operation";
import { valueFrom } from "../../../util";


export default function (ctx) {

    const chatClientId = ctx.params.id;
    const chatClient = el.state.create(null);

    function updateView() {
        runOperation("Fetching chat client...", async () => {
            const response = await api.getJson(`/api/v1/web-chat-client/${chatClientId}`);
            chatClient.value = response.data;
        });
    }

    updateView();

    function handleSave() {
        const data = {
            integrationType: "Zapi",
            integrations: {
                zapiIntegration: {
                    instanceId: valueFrom("zapiInstanceId"),
                    instanceToken: valueFrom("zapiInstanceToken"),
                    clientToken: valueFrom("zapiClientToken"),
                    sessionDuration: valueFrom("zapiSessionDuration")
                }
            }
        };

        runOperation("Saving Z-API integration details...", async () => {
            await api.putJson(`/api/v1/web-chat-client/${chatClientId}/integrations`, data);
        });
    }

    return el.state.listening([chatClient], () =>
        Dashboard(
            Section(
                Link(el.fragment(Icon("arrow-left-line"), "Return back"), null, {
                    onClick() {
                        history.go(-1);
                        page();
                    }
                })
            ),
            DashboardTitle(
                DashboardTextHeading("Z-API settings", chatClient.value?.name)
            ),
            Section(
                CommandBar(
                    Button(
                        Icon("save-line"),
                        "Save changes",
                        {
                            onClick: handleSave
                        }
                    )
                )
            ),
            Section(
                Card(
                    CardContent(
                        Paragraph(
                            "To set up a WhatsApp instance, you need to have an instance configured with your ",
                            "WhatsApp number and already connected in your panel ", ExternalLink("Z-API", "https://app.z-api.io/app/instances"), "."
                        ),
                        Paragraph(
                            "After that, set the URL below as the \"On receive\" webhook in the Z-API panel:"
                        ),
                        BlockCodeWrapper("Integration webhook",
                            window.env.apiEndpoint + "api/v1/intl/zapi/" + chatClientId + "/on-message-received", "url"
                        ),
                        Paragraph(
                            "Notes: uncheck the \"Notify those sent by me too\" checkbox. Activating this will cause the assistant ",
                            "to enter an infinite loop."
                        ),
                        Paragraph(
                            "Read the official documentation of ", ExternalLink("Z-API", "https://developer.z-api.io/"), " for more details."
                        ),
                        Paragraph(
                            "Note: AIVAX is not affiliated in any way with Z-API."
                        )
                    )
                )
            ),
            Section(
                Editor(
                    {
                        id: "zapiInstanceId",
                        value: chatClient.value?.integrations.zapiIntegration?.instanceId,
                        renderValue(value) {
                            return Button(value || MutedText("Empty"));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: TextInput(null, "3E4A27C228BB...6C182D", { value }),
                                onConfirm: callback,
                                validate: [
                                    [x => x.length > 3, "The instance ID name must be at least 3 characters long."]
                                ],
                                nullButton: true
                            })
                        }
                    },
                    EditorDescription(
                        EditorTitle("Instance ID"),
                        Paragraph("Specify the ID of the instance where WhatsApp is connected.")
                    )
                ),
                Editor(
                    {
                        id: "zapiInstanceToken",
                        value: chatClient.value?.integrations.zapiIntegration?.instanceToken,
                        renderValue(value) {
                            return Button("👀");
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: TextInput(null, "70803BD...3D22A", { value }),
                                onConfirm: callback,
                                validate: [
                                    [x => x.length > 3, "The instance token must be at least 3 characters long."]
                                ],
                                nullButton: true
                            })
                        }
                    },
                    EditorDescription(
                        EditorTitle("Instance token"),
                        Paragraph("Specify the secret token of the instance used to send messages.")
                    )
                ),
            ),
            Section(
                Editor(
                    {
                        id: "zapiClientToken",
                        value: chatClient.value?.integrations.zapiIntegration?.clientToken,
                        renderValue(value) {
                            return Button("👀");
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: TextInput(null, "Fef7bad85c...93dceaac2S", { value }),
                                onConfirm: callback,
                                validate: [
                                    [x => x.length > 3, "The client token must be at least 3 characters long."]
                                ],
                                nullButton: true
                            })
                        }
                    },
                    EditorDescription(
                        EditorTitle("Client token"),
                        Paragraph("Specify the secret token of the account for communication."),
                        Paragraph("This value is only necessary if it is active in your Z-API account.")
                    )
                ),
            ),
            Section(
                Editor(
                    {
                        id: "zapiSessionDuration",
                        value: chatClient.value?.integrations.zapiIntegration?.sessionDuration || 10800,
                        renderValue(value) {
                            return Button(formatDuration(value));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: TimeSpanPicker({ value: value }),
                                onConfirm: callback,
                                validate: [
                                    [x => x >= 600, "The duration must be greater than or equal to 10 minutes."],
                                    [x => x <= 7776000, "The duration must be less than or equal to 90 days."],
                                ]
                            })
                        }
                    },
                    EditorDescription(
                        EditorTitle("Session duration"),
                        Paragraph("Specify the duration that a conversation has before being restarted.")
                    )
                ),
            )
        )
    )
}